/**
 * ERP-FIN-02 Gate AR-01: AR Invoice Detail
 * Route: /ar/invoices/:invoiceId  (ar.invoice.view)
 *
 * Full invoice detail: header, lines, lifecycle actions, PDF download, audit trail.
 * Immutability enforced: ISSUED invoices are read-only; SoD enforced on Approve.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Download, CheckCircle2, XCircle, Clock, Send, Archive,
  Plus, Trash2, RefreshCw, FileText, ChevronDown, Building2, User,
  AlertTriangle, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arInvoiceApi, formatError } from "@/services/api";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null || minor === undefined) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const STATUS_CFG = {
  DRAFT:    { label: "Draft",    color: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE", Icon: Clock },
  APPROVED: { label: "Approved", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A", Icon: CheckCircle2 },
  ISSUED:   { label: "Issued",   color: "#166534", bg: "#F0FDF4", border: "#BBF7D0", Icon: Send },
  VOID:     { label: "Void",     color: "#6B7280", bg: "#F5F5F5", border: "#E5E7EB", Icon: Archive },
};

function StatusPill({ status }) {
  const c = STATUS_CFG[status] || { label: status, color: "#666", bg: "#F5F5F5", border: "#DDD", Icon: FileText };
  const Ic = c.Icon;
  return (
    <span data-testid="invoice-status-pill" style={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      padding: "5px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
    }}>
      <Ic size={12} /> {c.label}
    </span>
  );
}

const TAB_ITEMS = [
  { key: "details", label: "Invoice" },
  { key: "audit",   label: "Audit Trail" },
];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ARInvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invoice,    setInvoice]    = useState(null);
  const [lines,      setLines]      = useState([]);
  const [audit,      setAudit]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState("details");
  const [submitting, setSubmitting] = useState(false);

  // Modals
  const [showApprove, setShowApprove] = useState(false);
  const [showReject,  setShowReject]  = useState(false);
  const [showIssue,   setShowIssue]   = useState(false);
  const [showVoid,    setShowVoid]    = useState(false);

  // Modal fields
  const [approveNotes,   setApproveNotes]   = useState("");
  const [rejectReason,   setRejectReason]   = useState("");
  const [issueNotes,     setIssueNotes]     = useState("");
  const [voidReason,     setVoidReason]     = useState("");

  // Add Line (DRAFT only)
  const [showAddLine, setShowAddLine] = useState(false);
  const [taxCodes,    setTaxCodes]    = useState([]);
  const [lineForm, setLineForm] = useState({
    description: "", quantity: 1, unit_price_minor: "",
    tax_code_id: "", service_category: "",
  });

  const hasCap     = (cap) => user?.capabilities?.includes(cap);
  const canCreate  = hasCap("ar.invoice.create");
  const canApprove = hasCap("ar.invoice.approve");
  const canIssue   = hasCap("ar.invoice.issue");
  const canVoid    = hasCap("ar.invoice.void");

  const isDraft    = invoice?.status === "DRAFT";
  const isApproved = invoice?.status === "APPROVED";
  const isIssued   = invoice?.status === "ISSUED";
  const isVoid     = invoice?.status === "VOID";

  // Detect if current user is the creator (SoD)
  const isCreator  = invoice?.created_by === user?.id;

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await arInvoiceApi.get(invoiceId);
      const data = res.data;
      setInvoice(data);
      setLines(data.lines || []);
    } catch (err) {
      toast.error(formatError(err));
      if (err.response?.status === 404) navigate("/ar/invoices");
    } finally {
      setLoading(false);
    }
  }, [invoiceId, navigate]);

  const loadAudit = useCallback(async () => {
    if (tab !== "audit") return;
    try {
      const res = await arInvoiceApi.getAudit(invoiceId);
      setAudit(res.data || []);
    } catch { /* non-blocking */ }
  }, [invoiceId, tab]);

  const loadTaxCodes = useCallback(async () => {
    if (!showAddLine) return;
    try {
      const res = await axios.get(`${API}/api/finance/tax-codes`, { withCredentials: true });
      setTaxCodes(res.data?.items || res.data || []);
    } catch { /* non-blocking */ }
  }, [showAddLine]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAudit(); }, [loadAudit]);
  useEffect(() => { loadTaxCodes(); }, [loadTaxCodes]);

  // ── Lifecycle Actions ─────────────────────────────────────────────────────
  const doApprove = async () => {
    setSubmitting(true);
    try {
      await arInvoiceApi.approve(invoiceId, { approval_notes: approveNotes });
      toast.success("Invoice approved successfully");
      setShowApprove(false); setApproveNotes("");
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("Rejection reason is required"); return; }
    setSubmitting(true);
    try {
      await arInvoiceApi.reject(invoiceId, { rejection_reason: rejectReason });
      toast.success("Invoice returned to Draft");
      setShowReject(false); setRejectReason("");
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const doIssue = async () => {
    setSubmitting(true);
    try {
      await arInvoiceApi.issue(invoiceId, { issue_notes: issueNotes });
      toast.success("Invoice issued — PDF generated and archived");
      setShowIssue(false); setIssueNotes("");
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const doVoid = async () => {
    if (!voidReason.trim()) { toast.error("Void reason is required"); return; }
    setSubmitting(true);
    try {
      await arInvoiceApi.void(invoiceId, { void_reason: voidReason });
      toast.success("Invoice voided");
      setShowVoid(false); setVoidReason("");
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  // ── Line Management ───────────────────────────────────────────────────────
  const doAddLine = async (e) => {
    e.preventDefault();
    if (!lineForm.description || !lineForm.unit_price_minor) {
      toast.error("Description and unit price are required"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        description: lineForm.description,
        quantity: parseInt(lineForm.quantity, 10) || 1,
        unit_price_minor: Math.round(parseFloat(lineForm.unit_price_minor) * 100),
        tax_code_id: lineForm.tax_code_id || null,
        service_category: lineForm.service_category || null,
      };
      await arInvoiceApi.addLine(invoiceId, payload);
      toast.success("Line item added");
      setShowAddLine(false);
      setLineForm({ description: "", quantity: 1, unit_price_minor: "", tax_code_id: "", service_category: "" });
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const doDeleteLine = async (lid) => {
    if (!window.confirm("Remove this line item?")) return;
    try {
      await arInvoiceApi.deleteLine(invoiceId, lid);
      toast.success("Line removed");
      await load();
    } catch (err) { toast.error(formatError(err)); }
  };

  // ── PDF Download ──────────────────────────────────────────────────────────
  const openPdf = () => {
    const url = arInvoiceApi.getPdfUrl(invoiceId);
    window.open(url, "_blank");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ padding: "48px", textAlign: "center", color: "#6B7280" }}>Loading invoice…</div>
  );
  if (!invoice) return null;

  const currency = invoice.currency_code || "INR";

  return (
    <div data-testid="ar-invoice-detail" style={{ padding: "24px", maxWidth: "1100px", margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
        <button
          data-testid="back-btn"
          onClick={() => navigate("/ar/invoices")}
          style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: "13px" }}
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <h1 data-testid="invoice-number-heading" style={{ fontSize: "20px", fontWeight: 700, color: "#111827", margin: 0 }}>
            {invoice.invoice_number || "Invoice"}
          </h1>
          <StatusPill status={invoice.status} />
          {invoice.collection_status && invoice.status === "ISSUED" && (
            <span style={{ fontSize: "11px", padding: "3px 8px", borderRadius: "12px", background: "#FEF3C7", color: "#92400E", fontWeight: 600 }}>
              {invoice.collection_status.replace("_", " ")}
            </span>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {isIssued && (
            <button
              data-testid="download-pdf-btn"
              onClick={openPdf}
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 14px", background: "#F0FDF4", color: "#166534", border: "1px solid #BBF7D0", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
            >
              <Download size={13} /> Download PDF
            </button>
          )}
          {isDraft && canCreate && (
            <button
              data-testid="add-line-btn"
              onClick={() => setShowAddLine(true)}
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 14px", background: "#fff", color: "#374151", border: "1px solid #D1D5DB", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}
            >
              <Plus size={13} /> Add Line
            </button>
          )}
          {isDraft && canApprove && !isCreator && (
            <button
              data-testid="approve-btn"
              onClick={() => setShowApprove(true)}
              style={{ padding: "7px 14px", background: "#0176D3", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
            >
              Approve
            </button>
          )}
          {isDraft && canApprove && isCreator && (
            <span
              data-testid="sod-warning"
              title="Separation of Duties: you created this invoice and cannot approve it"
              style={{ padding: "7px 14px", background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A", borderRadius: "6px", fontSize: "12px", fontWeight: 600, cursor: "default" }}
            >
              SoD — Cannot Approve Own Invoice
            </span>
          )}
          {isApproved && canApprove && (
            <button
              data-testid="reject-btn"
              onClick={() => setShowReject(true)}
              style={{ padding: "7px 14px", background: "#fff", color: "#DC2626", border: "1px solid #FECACA", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}
            >
              Reject
            </button>
          )}
          {isApproved && canIssue && (
            <button
              data-testid="issue-btn"
              onClick={() => setShowIssue(true)}
              style={{ padding: "7px 14px", background: "#166534", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}
            >
              Issue Invoice
            </button>
          )}
          {!isVoid && !isIssued && canVoid && (
            <button
              data-testid="void-btn"
              onClick={() => setShowVoid(true)}
              style={{ padding: "7px 14px", background: "#fff", color: "#6B7280", border: "1px solid #E5E7EB", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}
            >
              Void
            </button>
          )}
          {isIssued && canVoid && (
            <button
              data-testid="void-issued-btn"
              onClick={() => setShowVoid(true)}
              style={{ padding: "7px 14px", background: "#fff", color: "#DC2626", border: "1px solid #FECACA", borderRadius: "6px", cursor: "pointer", fontSize: "13px" }}
            >
              Void (Post-Issue)
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", borderBottom: "1px solid #E5E7EB", marginBottom: "20px" }}>
        {TAB_ITEMS.map(t => (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            style={{
              padding: "8px 16px", background: "none", border: "none",
              borderBottom: tab === t.key ? "2px solid #0176D3" : "2px solid transparent",
              color: tab === t.key ? "#0176D3" : "#6B7280",
              fontWeight: tab === t.key ? 600 : 400, fontSize: "13px", cursor: "pointer",
              marginBottom: "-1px",
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* ── Tab: Invoice Details ── */}
      {tab === "details" && (
        <div>
          {/* Immutability notice for ISSUED */}
          {isIssued && (
            <div data-testid="immutability-notice" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", fontSize: "13px", color: "#166534", display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckCircle2 size={15} />
              This invoice has been ISSUED and is legally binding. It cannot be edited. The issued PDF snapshot is permanently archived.
            </div>
          )}
          {isVoid && (
            <div data-testid="void-notice" style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", fontSize: "13px", color: "#991B1B", display: "flex", alignItems: "flex-start", gap: "8px" }}>
              <XCircle size={15} style={{ marginTop: "1px" }} />
              <div><strong>VOID:</strong> {invoice.void_reason}</div>
            </div>
          )}

          {/* Header Info Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
            {/* Bill To */}
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "16px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>Bill To</div>
              <div style={{ fontSize: "15px", fontWeight: 700, color: "#111827" }}>{invoice.customer_name || "—"}</div>
              {invoice.customer_gstin && <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>GSTIN: {invoice.customer_gstin}</div>}
              {invoice.billing_address?.line1 && <div style={{ fontSize: "12px", color: "#374151", marginTop: "4px" }}>{invoice.billing_address.line1}</div>}
              {invoice.billing_address?.city && <div style={{ fontSize: "12px", color: "#374151" }}>{invoice.billing_address.city}</div>}
            </div>

            {/* Invoice Meta */}
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                {[
                  { label: "Invoice #",    value: invoice.invoice_number },
                  { label: "Invoice Date", value: fmtDate(invoice.invoice_date) },
                  { label: "Due Date",     value: fmtDate(invoice.due_date) },
                  { label: "Acct. Date",   value: fmtDate(invoice.accounting_date) },
                  { label: "Fiscal Period", value: invoice.period_name || "—" },
                  { label: "Currency",     value: invoice.currency_code },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <div style={{ fontSize: "11px", color: "#6B7280" }}>{label}</div>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{value || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* References */}
          {(invoice.event_name || invoice.proposal_id || invoice.legal_entity_name) && (
            <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", display: "flex", gap: "24px", flexWrap: "wrap", fontSize: "12px" }}>
              {invoice.legal_entity_name && (
                <div><span style={{ color: "#6B7280" }}>Legal Entity: </span><span style={{ fontWeight: 600, color: "#111827" }}>{invoice.legal_entity_name}</span></div>
              )}
              {invoice.event_name && (
                <div><span style={{ color: "#6B7280" }}>Event: </span><span style={{ fontWeight: 600, color: "#111827" }}>{invoice.event_name}</span></div>
              )}
              {invoice.proposal_id && (
                <div><span style={{ color: "#6B7280" }}>Proposal: </span><span style={{ fontWeight: 600, color: "#111827" }}>{invoice.proposal_id}</span></div>
              )}
            </div>
          )}

          {/* Line Items */}
          <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", overflow: "hidden", marginBottom: "16px" }}>
            <div style={{ padding: "12px 16px", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Line Items</span>
              {isDraft && canCreate && (
                <button data-testid="add-line-btn-inline" onClick={() => setShowAddLine(true)}
                  style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: "none", color: "#0176D3", cursor: "pointer", fontSize: "12px", fontWeight: 600 }}>
                  <Plus size={12} /> Add Line
                </button>
              )}
            </div>
            {lines.length === 0 ? (
              <div style={{ padding: "24px", textAlign: "center", color: "#9CA3AF", fontSize: "13px" }}>
                No line items yet. {isDraft && canCreate ? "Add a line item above." : ""}
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F9FAFB" }}>
                    {["#", "Description", "Qty", "Unit Price", "Tax", "Line Total", isDraft && canCreate ? "" : null].filter(Boolean).map(h => (
                      <th key={h} style={{ padding: "8px 12px", textAlign: h === "Qty" || h === "Unit Price" || h === "Tax" || h === "Line Total" ? "right" : "left", fontSize: "11px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", borderBottom: "1px solid #E5E7EB" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((ln, idx) => (
                    <tr key={ln.id} data-testid={`line-row-${ln.id}`} style={{ borderBottom: idx < lines.length - 1 ? "1px solid #F3F4F6" : "none" }}>
                      <td style={{ padding: "10px 12px", fontSize: "12px", color: "#6B7280" }}>{ln.line_number || idx + 1}</td>
                      <td style={{ padding: "10px 12px", fontSize: "13px", color: "#111827" }}>
                        <div>{ln.description}</div>
                        {ln.service_category && <div style={{ fontSize: "11px", color: "#6B7280" }}>{ln.service_category}</div>}
                        {ln.tax_description && <div style={{ fontSize: "11px", color: "#9CA3AF" }}>{ln.tax_description}</div>}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: "13px", color: "#374151", textAlign: "right" }}>{ln.quantity}</td>
                      <td style={{ padding: "10px 12px", fontSize: "13px", color: "#374151", textAlign: "right" }}>{fmt(ln.unit_price_minor, currency)}</td>
                      <td style={{ padding: "10px 12px", fontSize: "12px", color: "#6B7280", textAlign: "right" }}>{fmt(ln.line_tax_minor, currency)}</td>
                      <td style={{ padding: "10px 12px", fontSize: "13px", fontWeight: 600, color: "#111827", textAlign: "right" }}>{fmt(ln.line_total_minor, currency)}</td>
                      {isDraft && canCreate && (
                        <td style={{ padding: "10px 12px" }}>
                          <button data-testid={`delete-line-${ln.id}`} onClick={() => doDeleteLine(ln.id)}
                            style={{ background: "none", border: "none", color: "#DC2626", cursor: "pointer", padding: "2px" }}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Totals */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "16px", minWidth: "280px" }}>
              {[
                { label: "Subtotal", value: invoice.subtotal_minor },
                { label: "Tax",      value: invoice.tax_minor },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "13px", color: "#6B7280" }}>
                  <span>{label}</span>
                  <span>{fmt(value, currency)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", marginTop: "6px", borderTop: "2px solid #0176D3", fontSize: "16px", fontWeight: 700, color: "#111827" }}>
                <span>TOTAL</span>
                <span data-testid="invoice-total">{fmt(invoice.total_minor, currency)}</span>
              </div>
              {invoice.status === "ISSUED" && (
                <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: "12px", color: "#6B7280", marginTop: "4px" }}>
                  <span>Outstanding</span>
                  <span style={{ color: invoice.outstanding_minor > 0 ? "#DC2626" : "#166534", fontWeight: 600 }}>
                    {fmt(invoice.outstanding_minor, currency)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {invoice.invoice_notes && (
            <div style={{ marginTop: "16px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "12px 16px", fontSize: "13px", color: "#374151" }}>
              <span style={{ fontWeight: 600 }}>Notes: </span>{invoice.invoice_notes}
            </div>
          )}

          {/* Issue metadata */}
          {isIssued && (
            <div style={{ marginTop: "16px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", padding: "12px 16px", fontSize: "12px", color: "#374151" }}>
              <strong>Issued By:</strong> {invoice.issued_by_email || invoice.issued_by} &nbsp;·&nbsp;
              <strong>Issued At:</strong> {fmtDateTime(invoice.issued_at)} &nbsp;·&nbsp;
              <strong>Snapshot Hash:</strong> <code style={{ fontSize: "11px" }}>{invoice.issued_snapshot_hash?.slice(0, 16)}…</code>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Audit Trail ── */}
      {tab === "audit" && (
        <div data-testid="audit-trail-tab">
          {audit.length === 0 ? (
            <div style={{ padding: "32px", textAlign: "center", color: "#9CA3AF" }}>No audit events found.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {audit.map((ev, idx) => (
                <div key={ev.id || idx} data-testid={`audit-event-${idx}`} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "8px", padding: "12px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#0176D3", background: "#EFF6FF", padding: "2px 8px", borderRadius: "12px" }}>
                      {ev.event_type || ev.action}
                    </span>
                    <span style={{ fontSize: "11px", color: "#9CA3AF" }}>{fmtDateTime(ev.timestamp || ev.created_at)}</span>
                  </div>
                  <div style={{ fontSize: "13px", color: "#374151", marginTop: "6px" }}>{ev.summary || ev.description || "—"}</div>
                  {ev.actor_email && <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "3px" }}>By: {ev.actor_email}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Modals ── */}

      {/* Approve */}
      {showApprove && (
        <Modal title="Approve Invoice" onClose={() => setShowApprove(false)}>
          <p style={{ fontSize: "13px", color: "#374151", marginBottom: "12px" }}>
            Approve <strong>{invoice.invoice_number}</strong> for {fmt(invoice.total_minor, currency)}?
            Once approved, it can be issued as a legally binding document.
          </p>
          <label style={labelStyle}>Approval Notes (optional)</label>
          <textarea data-testid="approve-notes-input" value={approveNotes} onChange={e => setApproveNotes(e.target.value)}
            rows={2} placeholder="Optional notes…"
            style={{ ...inputStyle, resize: "vertical", width: "100%", boxSizing: "border-box" }} />
          <ModalButtons onCancel={() => setShowApprove(false)} onConfirm={doApprove}
            confirmLabel="Approve" confirmColor="#0176D3" loading={submitting} testId="confirm-approve-btn" />
        </Modal>
      )}

      {/* Reject */}
      {showReject && (
        <Modal title="Reject Invoice" onClose={() => setShowReject(false)}>
          <p style={{ fontSize: "13px", color: "#374151", marginBottom: "12px" }}>
            Return <strong>{invoice.invoice_number}</strong> to DRAFT with a correction reason.
          </p>
          <label style={labelStyle}>Rejection Reason *</label>
          <textarea data-testid="reject-reason-input" value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            rows={3} required placeholder="Explain what needs to be corrected…"
            style={{ ...inputStyle, resize: "vertical", width: "100%", boxSizing: "border-box" }} />
          <ModalButtons onCancel={() => setShowReject(false)} onConfirm={doReject}
            confirmLabel="Reject" confirmColor="#DC2626" loading={submitting} testId="confirm-reject-btn" />
        </Modal>
      )}

      {/* Issue */}
      {showIssue && (
        <Modal title="Issue Invoice" onClose={() => setShowIssue(false)}>
          <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "6px", padding: "10px 12px", marginBottom: "12px", fontSize: "12px", color: "#92400E" }}>
            <strong>Irreversible:</strong> Issuing generates an immutable PDF, archives it in the DCC, and marks the invoice as legally binding. This action cannot be undone without voiding.
          </div>
          <p style={{ fontSize: "13px", color: "#374151", marginBottom: "12px" }}>
            Issue <strong>{invoice.invoice_number}</strong> to <strong>{invoice.customer_name}</strong> for <strong>{fmt(invoice.total_minor, currency)}</strong>?
          </p>
          <label style={labelStyle}>Issue Notes (optional)</label>
          <textarea data-testid="issue-notes-input" value={issueNotes} onChange={e => setIssueNotes(e.target.value)}
            rows={2} placeholder="Optional delivery notes…"
            style={{ ...inputStyle, resize: "vertical", width: "100%", boxSizing: "border-box" }} />
          <ModalButtons onCancel={() => setShowIssue(false)} onConfirm={doIssue}
            confirmLabel="Issue Invoice" confirmColor="#166534" loading={submitting} testId="confirm-issue-btn" />
        </Modal>
      )}

      {/* Void */}
      {showVoid && (
        <Modal title="Void Invoice" onClose={() => setShowVoid(false)}>
          {invoice.status === "ISSUED" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "6px", padding: "10px 12px", marginBottom: "12px", fontSize: "12px", color: "#991B1B" }}>
              <strong>Warning:</strong> Voiding an ISSUED invoice is a significant financial action. Consider raising a Credit Note instead for legally compliant corrections.
            </div>
          )}
          <label style={labelStyle}>Void Reason *</label>
          <textarea data-testid="void-reason-input" value={voidReason} onChange={e => setVoidReason(e.target.value)}
            rows={3} required placeholder="Mandatory reason for voiding this invoice…"
            style={{ ...inputStyle, resize: "vertical", width: "100%", boxSizing: "border-box" }} />
          <ModalButtons onCancel={() => setShowVoid(false)} onConfirm={doVoid}
            confirmLabel="Void Invoice" confirmColor="#DC2626" loading={submitting} testId="confirm-void-btn" />
        </Modal>
      )}

      {/* Add Line */}
      {showAddLine && (
        <Modal title="Add Line Item" onClose={() => setShowAddLine(false)}>
          <form onSubmit={doAddLine}>
            <label style={labelStyle}>Description *</label>
            <input data-testid="line-description-input" type="text" required value={lineForm.description}
              onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Service description…"
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "12px" }}>
              <div>
                <label style={labelStyle}>Quantity</label>
                <input data-testid="line-qty-input" type="number" min="1" value={lineForm.quantity}
                  onChange={e => setLineForm(f => ({ ...f, quantity: e.target.value }))}
                  style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={labelStyle}>Unit Price (₹) *</label>
                <input data-testid="line-price-input" type="number" min="0" step="0.01" required value={lineForm.unit_price_minor}
                  onChange={e => setLineForm(f => ({ ...f, unit_price_minor: e.target.value }))}
                  placeholder="0.00"
                  style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
                <p style={{ fontSize: "11px", color: "#6B7280", margin: "2px 0 0" }}>Enter in rupees (e.g. 5000 for ₹5,000)</p>
              </div>
            </div>

            <div style={{ marginTop: "12px" }}>
              <label style={labelStyle}>Tax Code</label>
              <select data-testid="line-tax-select" value={lineForm.tax_code_id}
                onChange={e => setLineForm(f => ({ ...f, tax_code_id: e.target.value }))}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}>
                <option value="">— No Tax —</option>
                {taxCodes.map(tc => (
                  <option key={tc._id || tc.id} value={tc._id || tc.id}>
                    {tc.code} — {tc.description || tc.name} ({(tc.rate_bps / 100).toFixed(0)}%)
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
              <button type="button" onClick={() => setShowAddLine(false)}
                style={{ padding: "7px 16px", border: "1px solid #D1D5DB", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" }}>
                Cancel
              </button>
              <button type="submit" data-testid="add-line-submit" disabled={submitting}
                style={{ padding: "7px 16px", background: submitting ? "#93C5FD" : "#0176D3", color: "#fff", border: "none", borderRadius: "6px", cursor: submitting ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
                {submitting ? "Adding…" : "Add Line"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────
const labelStyle = { display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
const inputStyle = { padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px" };

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ background: "#fff", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: "480px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#111827", marginBottom: "16px", marginTop: 0 }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function ModalButtons({ onCancel, onConfirm, confirmLabel, confirmColor, loading, testId }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
      <button onClick={onCancel} style={{ padding: "7px 16px", border: "1px solid #D1D5DB", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" }}>
        Cancel
      </button>
      <button data-testid={testId} onClick={onConfirm} disabled={loading}
        style={{ padding: "7px 16px", background: loading ? "#9CA3AF" : confirmColor, color: "#fff", border: "none", borderRadius: "6px", cursor: loading ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
        {loading ? "Processing…" : confirmLabel}
      </button>
    </div>
  );
}
